import React, { useMemo } from 'react';
import type {
  WebsiteReadResponse,
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  filterWebsiteTemplatesForPicker,
  isWebsiteTemplateInUse,
  resolveWebsiteTemplateBadge,
  sortWebsiteTemplatesForPicker,
} from '../../utils/website-template-catalog.util';
import { WebsiteTemplateCard } from './website-template-card';

/** Props for the design picker. */
interface WebsiteTemplatePickerProps {
  /** Designs the tenant may use. */
  templates: WebsiteTemplateRegistryTemplateSummary[];
  /** Design the draft uses, when it is still selectable. */
  selectedTemplate?: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand personalizes every card. */
  form: WebsiteUpsertRequest;
  /** Current website, used to mark the design in use. */
  website?: WebsiteReadResponse | null;
  /** Whether design changes are blocked right now. */
  disabled: boolean;
  /** Reference time for the "new" tag. */
  now?: Date;
  /** Called with the design the user tries. */
  onSelect: (template: WebsiteTemplateRegistryTemplateSummary) => void;
}

/**
 * Lists the selectable designs as cards with live thumbnails, tags, and
 * actions to try a design or open its example. Legacy designs appear only
 * for the agencies that use them.
 *
 * Technical identifiers such as keys, versions, and registry visibility stay
 * out of the cards.
 *
 * @param props - Designs, draft, website, and selection callback.
 * @returns A responsive grid of design cards, or an empty state.
 */
export const WebsiteTemplatePicker: React.FC<WebsiteTemplatePickerProps> = ({
  templates,
  selectedTemplate,
  form,
  website,
  disabled,
  now,
  onSelect,
}) => {
  const { t } = useI18n();
  const savedTemplateKey = website?.templateKey;
  const draftTemplateKey = form.templateKey;
  const orderedTemplates = useMemo(
    () =>
      sortWebsiteTemplatesForPicker(
        filterWebsiteTemplatesForPicker(templates, [
          savedTemplateKey,
          draftTemplateKey,
        ])
      ),
    [templates, savedTemplateKey, draftTemplateKey]
  );
  const referenceTime = now ?? new Date();

  if (orderedTemplates.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-6">
        <Text size="small" color="neutral">
          {t('websites.settings.design.empty')}
        </Text>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {orderedTemplates.map((template) => (
        <WebsiteTemplateCard
          key={template.registryTemplateId}
          template={template}
          form={form}
          badge={resolveWebsiteTemplateBadge(template, referenceTime)}
          inUse={isWebsiteTemplateInUse(template, website)}
          selected={template === selectedTemplate}
          disabled={disabled}
          onSelect={() => onSelect(template)}
        />
      ))}
    </div>
  );
};
